import { Sentry } from '../../entry-setup/sentry';
import { firstServerError } from '../../util/api-errors';

export function editMessageActions() {
  return {
    editingMessage: null,
    isSavingEdit: false,
    editMessageError: null,

    // Author only, text messages only, and only while nobody else has read it.
    // The enclosing template already excludes deleted/hidden/pending messages.
    canEditMessage(message) {
      return (
        this.isOwnMessage(message) &&
        message.message_type === 'text' &&
        !message.attachment_type &&
        !this.messageReadByOthers(message)
      );
    },

    // The composer listens for this event to prefill itself with the
    // message's current content (see messageInput#startEdit).
    startEditMessage(message) {
      this.editingMessage = message;
      this.$dispatch('chat:edit-message', { message });

      // The edit banner makes the composer taller, which can push the message
      // being edited out of view. Bring it back so it stays readable while its
      // text is being changed, without the user having to scroll.
      this.$nextTick(() => {
        this.$refs.messageContainer
          ?.querySelector(`[data-message-id="${message.id}"]`)
          ?.scrollIntoView({ block: 'nearest' });
      });
    },

    cancelEditMessage() {
      this.editingMessage = null;
    },

    async submitEditMessage(content) {
      if (!this.editingMessage) return;

      this.isSavingEdit = true;
      const messageId = this.editingMessage.id;

      try {
        const response = await this.$api({
          url: `/chat/conversations/${this.activeConversationId}/messages/${messageId}`,
          method: 'PATCH',
          data: { content }
        });

        this.handleMessageEdited(this.activeConversationId, response.data.message);
        this.trackChat('message', 'edit');
        this.editingMessage = null;
      } catch (err) {
        // The most common failure is "already read" — the recipient read the
        // message in the window before the request landed, so it can no longer
        // be edited. Surface the server's message as a toast and leave edit mode.
        this.editingMessage = null;

        const serverError = await firstServerError(err);
        if (!serverError) {
          // Unexpected failure (network, 5xx, etc.) — keep reporting to Sentry.
          Sentry.captureException(err, {
            extra: {
              message: 'Failed to edit message',
              conversationId: this.activeConversationId,
              messageId
            }
          });
        }

        this.showTransientError(
          'editMessageError',
          serverError || window.i18n?.t('chat.edit_message.edit_failed')
        );
      } finally {
        this.isSavingEdit = false;
      }
    }
  };
}
